import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { catchError, throwError } from 'rxjs';
import { TokenService } from './services/token.service';
import { CityService } from './services/city.service';

// External hosts the passengers app is allowed to contact via HttpClient.
// Leaflet tile servers are excluded — Leaflet fetches tiles via <img>, not HttpClient.
const ALLOWED_EXTERNAL_HOSTS = [
  'syncc.me',
  'dev.syncc.me',
  'nominatim.openstreetmap.org',
  'valhalla1.openstreetmap.de',
  'router.project-osrm.org',
];

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const router       = inject(Router);
  const tokenService = inject(TokenService);
  const cityService  = inject(CityService);

  // Runtime API base = the active city's silo (falls back to build-time apiUrl).
  const base      = cityService.apiBaseUrl();
  const buildBase = environment.apiUrl;

  const isLocalAsset  = req.url.startsWith('assets') || req.url.startsWith('./assets');
  const isHttp        = req.url.startsWith('http');
  const isBuildBase   = isHttp && req.url.startsWith(buildBase);   // absolute call using the baked URL
  const isExternalUrl = isHttp && !isBuildBase;                    // some other host (Nominatim, OSRM, cities.json…)

  // Block external calls to hosts not on the allowlist
  if (isExternalUrl && !isLocalAsset) {
    const host = new URL(req.url).hostname;
    if (!ALLOWED_EXTERNAL_HOSTS.includes(host)) {
      console.error(`[Interceptor] Blocked external request to disallowed host: ${host}`);
      return throwError(() => new Error(`Blocked: external host '${host}' is not allowed`));
    }
  }

  // Route everything that targets our API to the active city's base URL.
  let url = req.url;
  if (isLocalAsset || isExternalUrl) {
    url = req.url;
  } else if (isBuildBase) {
    url = base + req.url.slice(buildBase.length);
  } else {
    url = `${base}${req.url.startsWith('/') ? '' : '/'}${req.url}`;
  }

  const token = tokenService.getToken();

  // Only add headers to our own API calls — never to external URLs (e.g. cities.json, Nominatim, OSRM)
  let headers = req.headers;
  if (!isLocalAsset && !isExternalUrl) {
    headers = headers.set('ngrok-skip-browser-warning', 'true');
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
  }

  const cloned = req.clone({ url, headers });

  return next(cloned).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // Token expired — clear it but DON'T redirect (passengers are guests by default)
        tokenService.clearToken();
      }
      return throwError(() => error);
    }),
  );
};
